// ─────────────────────────────────────────────────────────────
// RiskAnalysis.js – Mantine “Scandi” version (2025-07-20)
// • Shows the single chosen risk **or** the ScenarioSelector UI
//   for picking / refining a variant.
// ─────────────────────────────────────────────────────────────
import React, { useState, useEffect }                 from 'react';
import { authFetch } from '../utils/authFetch';
import { useSelector, useDispatch }        from 'react-redux';
import {
  Card, Text, Button, Textarea, Collapse, Badge, ActionIcon, Stack, Group,
  Container, Title, Avatar, Divider, Alert, Loader,
} from '@mantine/core';
import {
  IconDeviceFloppy, IconTrash, IconBulb,
  IconCheck, IconEdit, IconInfoCircle, IconAlertCircle
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import {
  moveRiskToSaved, discardSavedRisk, discardRisk, updateSavedRisk,
} from '../redux/slices/risksSlice';
import useSaveOperation      from '../useSaveOperation';
import ScenarioSelector      from './ScenarioSelector';
import { Navigation } from '../components/NavigationButtons';

export default function RiskAnalysis({
  modelId,
  assessmentUuid,
  riskArea,
  source,
  modelNarratives = {},
  nextStep = "Continue to Risk Evaluation",
  onNext,
  onBack,
}) {
  const dispatch              = useDispatch();
  const { handleSaveOperation } = useSaveOperation();

  /* ── final single-risk (if already promoted from variants) ─────────── */
  const risk = useSelector(
    s =>
      s.risks.byAssessment[assessmentUuid]
        ?.modelRisks?.[riskArea]
        ?.areaRisks?.[source.name]?.[0]
  ) || {};

  /* ── local state for inline manual edit ────────────────────────────── */
  const [editOpen,     setEditOpen]   = useState(false);
  const [editedName,   setEditedName] = useState(risk.riskName || '');
  const [savingEdit,   setSavingEdit] = useState(false);
  
  // Update editedName when risk changes
  useEffect(() => {
    setEditedName(risk.riskName || '');
  }, [risk.riskName]);


  /* ── quick helpers ─────────────────────────────────────────────────── */
  const getStatusConfig = (status) => {
    if (status === 'Saved')  return { color: 'green',  label: 'Saved'  };
    if (status === 'Draft')  return { color: 'yellow', label: 'Draft'  };
    return { color: 'gray',  label: 'Unknown' };
  };
  const statusCfg = getStatusConfig(risk.status);

  /* ── actions ───────────────────────────────────────────────────────── */
  const saveRisk = async () => {
    if (risk.status === 'Saved') return;
    
    await handleSaveOperation(async () => {
      try {
        // First save to backend
        const response = await authFetch('/.netlify/functions/saveRisk', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            riskId: risk.riskId,
            name: risk.riskName,
            section: riskArea,
            category: risk.category,
            type: risk.type || 'Manual',
            rag_source_file: risk.rag_source_file || null,
            modelUuid: modelId,
            assessmentUuid,
            owner: risk.owner || { name: 'Unknown', email: 'unknown@email.com', picture: '' }
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to save risk: ${response.status}`);
        }

        // Only update Redux after successful backend save
        dispatch(
          moveRiskToSaved({
            assessmentUuid,
            sectionName: riskArea,
            risk: {
              ...risk,
              assessmentUuid,
              status: 'Saved',
            },
          })
        );

        notifications.show({
          title: 'Risk Saved',
          message: 'Risk has been successfully saved',
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      } catch (error) {
        notifications.show({
          title: 'Save Failed',
          message: `Failed to save risk: ${error.message}`,
          color: 'red',
          icon: <IconAlertCircle size={16} />,
        });
        throw error;
      }
    });
  };

  const discard = () => {
    if (risk.status === 'Saved') {
      dispatch(discardSavedRisk({ assessmentUuid, sectionName: riskArea, riskId: risk.riskId }));
    } else {
      dispatch(discardRisk({ assessmentUuid, sectionName: riskArea, risk }));
    }
    notifications.show({
      title: 'Risk Discarded',
      message: 'Risk has been removed',
      color: 'orange',
    });
  };


  // ── Save manual edit to risk name ─────────────────────────────
  const saveEditedRisk = async () => {
    if (!editedName.trim() || savingEdit) return;
    setSavingEdit(true);
    try {
      dispatch(
        updateSavedRisk({
          assessmentUuid,
          sectionName: riskArea,
          riskId: risk.riskId,
          category: risk.category,
          newDetails: { ...risk, riskName: editedName.trim() },
        })
      );
      notifications.show({
        title  : 'Risk Updated',
        message: 'Changes saved successfully',
        color  : 'green',
        icon   : <IconCheck size={16}/>
      });
    } finally {
      setSavingEdit(false);
      setEditOpen(false);
    }
  };


  /* ── UI ────────────────────────────────────────────────────────────── */
  return (
    <Container fluid px="xl" py="xl">
      {/* Header */}
      <Stack spacing="md" mb="xl">
        <Title order={3}>Risk Identification & Refinement</Title>
        <Group spacing="sm">
          <Text size="md" color="dimmed">Analyzing risk source:</Text>
          <Badge variant="filled" size="lg">{source.name}</Badge>
        </Group>
      </Stack>

      {risk.riskName ? (
        /* ── FINAL RISK CARD ─────────────────────────────────────────── */
        <>
          <Stack spacing="lg">
            <Card withBorder radius="md" p="xl">
              <Stack spacing="xs" mb="lg">
                <Text size="xs" color="dimmed" tt="uppercase">Identified Risk</Text>
                <Title order={4}>{risk.refinedRisk || risk.riskName}</Title>
                
                {/* Refinement rationale - professional left-aligned display */}
                {risk.refineRationale && (
                  <Group spacing="xs" mt="xs" align="flex-start">
                    <ActionIcon size="xs" color="blue" variant="light" radius="xl" style={{ marginTop: 2 }}>
                      <IconBulb size={10} />
                    </ActionIcon>
                    <Text size="xs" color="dimmed" fs="italic" style={{ textAlign: 'left', lineHeight: 1.4 }}>
                      {risk.refineRationale}
                    </Text>
                  </Group>
                )}
              </Stack>

              <Divider mb="lg" />


              <Group position="apart" align="flex-start">
                <Group spacing="xl">
                  {/* status */}
                  <Group spacing="xs">
                    <Text size="sm" color="dimmed">Status:</Text>
                    <Badge size="md" variant="light" color={statusCfg.color}>
                      {statusCfg.label}
                    </Badge>
                  </Group>

                  {/* owner */}
                  {risk.owner && (
                    <Group spacing="xs">
                      <Text size="sm" color="dimmed">Owner:</Text>
                      <Avatar src={risk.owner.picture} size={24} radius="xl" />
                      <Text size="sm">{risk.owner.name}</Text>
                    </Group>
                  )}
                </Group>

                {/* actions */}
                <Group spacing="xs">
                  <ActionIcon
                    onClick={() => setEditOpen(o => !o)}
                    variant={editOpen ? 'filled' : 'light'}
                    color={editOpen ? 'indigo' : 'gray'}
                    title="Edit risk name"
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    disabled={risk.status === 'Saved'}
                    onClick={saveRisk}
                    variant={risk.status === 'Saved' ? 'filled' : 'light'}
                    color={risk.status === 'Saved' ? 'green' : 'gray'}
                  >
                    {risk.status === 'Saved' ? <IconCheck size={16} /> : <IconDeviceFloppy size={16} />}
                  </ActionIcon>
                  <ActionIcon variant="light" color="red" onClick={discard}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>

            {/* Inline edit panel */}
            <Collapse in={editOpen}>
              <Card withBorder radius="md" p="xl" mt="md">
                <Group spacing="sm" mb="lg">
                  <Title order={5}>Edit Risk Name</Title>
                </Group>
                <Textarea
                  placeholder="Edit risk name…"
                  value={editedName}
                  onChange={e => setEditedName(e.currentTarget.value)}
                  minRows={2}
                  mb="md"
                />
                <Group position="right">
                  <Button variant="light" onClick={() => {setEditOpen(false); setEditedName(risk.riskName);} }>
                    Cancel
                  </Button>
                  <Button
                    disabled={!editedName.trim() || savingEdit}
                    leftIcon={savingEdit ? <Loader size={16}/> : <IconDeviceFloppy size={16}/>}
                    onClick={saveEditedRisk}
                  >
                    {savingEdit ? 'Saving…' : 'Save'}
                  </Button>
                </Group>
              </Card>
            </Collapse>

            <Alert
              icon={<IconInfoCircle size={16} />}
              color="indigo"
              variant="light"
            >
              Use the AI tool to add context, quantify impact, or tailor language.
            </Alert>
          </Stack>

          {/* nav */}
          <Navigation
            onBack={onBack}
            onNext={onNext}
            nextLabel={nextStep}
            nextDisabled={!risk.riskName}
            showBack={!!onBack}
          />
        </>
      ) : (
        /* ── NO RISK YET → SHOW SCENARIO SELECTOR ───────────────────── */
        <ScenarioSelector
          modelId={modelId}
          assessmentUuid={assessmentUuid}
          riskArea={riskArea}
          source={source}
          modelNarratives={modelNarratives}
          onDone={onNext}          /* when user promotes a variant */
        />
      )}
    </Container>
  );
}